import React from 'react';
import styled from 'styled-components';
import { Icon, Breakpoints, KineduPalette } from 'kinedu-react-components';
import { useRouter } from 'routes';
import LoadingSkeleton from '../catalog/CatalogLoading';

export interface ILinks {
  name: string;
  url?: string;
  goBack?: boolean;
  params?: any;
}

interface IBreadcrumbs {
  links: Array<ILinks>;
  isLoading?: boolean;
  color?: string;
  onClick?: (e: ILinks) => void;
  className?: string;
}

interface ICrumbs {
  hasLink: boolean;
  color?: string;
}

// eslint-disable-next-line
const Crumbs = styled.span <ICrumbs> `
  color: ${({ color }) => (color || KineduPalette.shadeDark)};
  font-size: 16px;
  letter-spacing: 0;
  font-weight: 500;
  cursor: ${({ hasLink }) => (hasLink ? 'pointer' : 'initial')};
  display: flex;
  align-items: center;
  padding-block: 20px;

  .skeleton-content-bar {
    margin-bottom: 0;

    .bar {
      height: 23px;
    }
  }
  display: none;
  &:last-child {
    display: flex;
  }
  ${Breakpoints.desktop} {
    display: block;
  }
`;

const BreadcrumbsContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 0;

  .breadcrumb-back-icon {
    margin-right: 10px;
  }
  ${Breakpoints.desktop} {
    margin: 0 0 20px;
    .breadcrumbs-crumb + .breadcrumbs-crumb::before {
      content: '/';
      display: inline-block;
      padding-left: 9px;
      padding-right: 9px;
    }
  }
`;

const Breadcrumbs: React.FC<IBreadcrumbs> = (props) => {
  const { links, isLoading, color, onClick, className } = props;
  const { history, appendParams, currentParamsRef } = useRouter();

  const lastLink = () => {
    const last = { pathname: '', search: undefined };
    [...links].reverse().some((l: ILinks) => {
      if (l.url) {
        last.pathname = l.url;
        last.search = appendParams(currentParamsRef.current, l.params);
      }
      return l.url;
    });

    return last;
  };

  const localHandleOnClick = async (link: ILinks) => {
    const { goBack, url } = link;
    if (goBack) {
      history.go(-2);
    } else if (url) {
      if (link?.params) {
        history.push({
          pathname: url,
          search: appendParams(currentParamsRef.current, link.params),
        });
      } else {
        history.push(url);
      }
    }
  };

  /**
   * Conditional function of the breadcrumbs
   */
  const handleOnClick = typeof onClick === 'function' ? onClick : localHandleOnClick;
  const localHandleOnClickBack = () => (lastLink() ? history.push(lastLink()) : {});
  /**
   * Conditional function of the back button
   */
  const handleOnClickBack = () => {
    if (typeof onClick === 'function') {
      return onClick({ ...[...links].reverse()[0], goBack: true });
    }
    return localHandleOnClickBack();
  };

  return (
    <BreadcrumbsContainer className={`breadcrumbs-container ${className}`}>
      <Icon
        size={26}
        name="arrow"
        flipHorizontally
        color={color || KineduPalette.shadeDark}
        className="breadcrumb-back-icon"
        onClick={handleOnClickBack}
      />

      {links.map((link: ILinks) => (
        <Crumbs
          key={link.name}
          hasLink={!!link?.url || !!link?.goBack}
          className="breadcrumbs-crumb"
          onClick={() => handleOnClick(link)}
          color={color}
        >
          {isLoading ? (
            <LoadingSkeleton bars={Array(1).fill('100px')} />
          ) : (
            link.name
          )}
        </Crumbs>
      ))}
    </BreadcrumbsContainer>
  );
};

export default Breadcrumbs;
